// src/pages/CategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package, ChevronDown, ChevronRight, Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import CategoryDialog from '../components/admin/CategoryDialog';
import CategoryImportDialog from '../components/admin/CategoryImportDialog';
import { categoryApi } from '../api';

const CategoriesPage = () => {
  const { language } = useApp();
  const t = translations[language];

  const [treeData, setTreeData] = useState([]);   // 树形结构（用于展示）
  const [flatData, setFlatData] = useState([]);   // 扁平列表（用于选父级）
  const [stats, setStats] = useState([]);          // 统计数据
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [defaultParentId, setDefaultParentId] = useState(null); // 点"+"时预填父级
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set()); // 展开的一级分类

  const loadCategories = async () => {
    setLoading(true);
    setError('');
    try {
      const [listRes, statsRes] = await Promise.all([
        categoryApi.getList(),
        categoryApi.getStats()
      ]);

      if (listRes.success) {
        setTreeData(listRes.data || []);
        setFlatData(listRes.flat || []);
        // 默认展开所有一级分类
        const parentIds = (listRes.data || []).map(c => c.id);
        setExpandedIds(new Set(parentIds));
      }
      if (statsRes.success) {
        setStats(statsRes.data || []);
      }
    } catch (err) {
      setError(err.message || '获取分类失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  // 获取统计信息
  const getStat = (id) => stats.find(s => s.id === id) || { product_count: 0, total_stock: 0, total_value: 0 };

  // 按当前POS界面语言，取对应的分类名称——某个语言没填翻译，自动退回中文，
  // 之前这里只判断了en/km两种，现在统一按字段映射表处理，
  // 新增语言（ms/zh_tw）不用再来回改这个函数
  const CATEGORY_NAME_FIELD_MAP = { zh: 'name', en: 'name_en', km: 'name_km', ms: 'name_ms', zh_tw: 'name_zh_tw' };
  const getCategoryName = (category) => {
    if (!category) return '';
    const field = CATEGORY_NAME_FIELD_MAP[language] || 'name';
    return category[field] || category.name;
  };

  // 导出——列结构和 CategoryImportDialog 的导入模板完全一致（大类/子类各5种语言），
  // 这样导出的文件可以直接拿到另一个租户那边导入，不用中间再手动调整格式。
  // 每个子分类单独一行，重复带上它所属的大类信息；没有子分类的大类，单独占一行
  // （子类那几列留空，导入时会被识别成"只建大类，不建子类"）。
  // 第1行表头、第2行示例——和导入模板同一套约定，导入时这两行会被自动跳过，
  // 所以留着不影响导入结果，方便打开文件的人一眼看懂每列是什么
  const handleExport = () => {
    const headers = [
      '大类(中)', '大类(英)', '大类(高棉语)', '大类(马来语)', '大类(繁体中文)',
      '子类(中)', '子类(英)', '子类(高棉语)', '子类(马来语)', '子类(繁体中文)'
    ];
    const example = [
      '示例：饮料', 'Beverages', 'ភេសជ្ជៈ', 'Minuman', '飲料',
      '碳酸饮料', 'Carbonated Drinks', 'ភេសជ្ជៈមានហ្គាស', 'Minuman Berkarbonat', '碳酸飲料'
    ];
    const dataRows = [];
    treeData.forEach(parent => {
      const parentCols = [parent.name || '', parent.name_en || '', parent.name_km || '', parent.name_ms || '', parent.name_zh_tw || ''];
      const children = parent.children || [];
      if (children.length === 0) {
        dataRows.push([...parentCols, '', '', '', '', '']);
      } else {
        children.forEach(child => {
          dataRows.push([...parentCols, child.name || '', child.name_en || '', child.name_km || '', child.name_ms || '', child.name_zh_tw || '']);
        });
      }
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, example, ...dataRows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'categories');
    XLSX.writeFile(wb, `分类导出_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAdd = (parentId = null) => {
    setEditingCategory(null);
    setDefaultParentId(parentId);
    setShowDialog(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setDefaultParentId(null);
    setShowDialog(true);
  };

  const handleDelete = async (id, name, productCount, childCount = 0) => {
    if (childCount > 0) {
      alert(`「${name}」下还有 ${childCount} 个子分类，请先删除`);
      return;
    }
    if (productCount > 0) {
      alert(`「${name}」下还有 ${productCount} 个商品，无法删除`);
      return;
    }
    if (!window.confirm(`确定要删除分类「${name}」吗？`)) return;

    try {
      const res = await categoryApi.delete(id);
      if (res.success) { loadCategories(); }
      else alert(res.message || '删除失败');
    } catch (err) {
      alert(err.message || '删除失败');
    }
  };

  const handleSubmit = async (formData) => {
    try {
      const res = editingCategory
        ? await categoryApi.update(editingCategory.id, formData)
        : await categoryApi.create(formData);

      if (res.success) {
        loadCategories();
        setShowDialog(false);
        setEditingCategory(null);
      } else {
        alert(res.message || '操作失败');
      }
    } catch (err) {
      alert(err.message || '操作失败');
    }
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 */}
      <div className="flex-shrink-0 flex justify-between items-center">
  <div>
    <h2 className="text-xl font-bold text-gray-800">
      {language === 'en' ? 'Category Management' :
       language === 'km' ? 'គ្រប់គ្រងប្រភេទ' :
       '分类管理'}
    </h2>
    <p className="text-xs text-gray-500 mt-1">
      {language === 'en'
        ? `${treeData.length} categories, ${flatData.filter(c => c.level === 2).length} subcategories`
        : `共 ${treeData.length} 个大类，${flatData.filter(c => c.level === 2).length} 个子分类`}
    </p>
  </div>
  <div className="flex items-center space-x-2">
    <button onClick={handleExport}
      className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 flex items-center">
      <Download className="w-4 h-4 mr-1" />
      {language === 'en' ? 'Export' : language === 'km' ? 'នាំចេញ' : '导出'}
    </button>
    <button onClick={() => setShowImportDialog(true)}
      className="bg-white border border-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 flex items-center">
      <Upload className="w-4 h-4 mr-1" />
      {language === 'en' ? 'Batch Import' : language === 'km' ? 'នាំចូលច្រើន' : '批量导入'}
    </button>
    <button onClick={() => handleAdd(null)}
      className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center">
      <Plus className="w-4 h-4 mr-1" />
      {language === 'en' ? 'Add Category' : language === 'km' ? 'បន្ថែមប្រភេទ' : '新增大类'}
    </button>
  </div>
</div>

{error && (
  <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
    {error}
    <button onClick={loadCategories} className="ml-4 underline">
      {language === 'en' ? 'Retry' : language === 'km' ? 'ព្យាយាមម្ដងទៀត' : '重试'}
    </button>
  </div>
)}
      {/* 表格 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow-lg flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">分类名称</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    <Package className="w-4 h-4 inline mr-1" />商品数
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">总库存</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">总价值</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {treeData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center text-gray-400">暂无分类</td>
                  </tr>
                ) : (
                  treeData.map(parent => {
                    const parentStat = getStat(parent.id);
                    const isExpanded = expandedIds.has(parent.id);
                    const children = parent.children || [];

                    return (
                      <React.Fragment key={parent.id}>
                        {/* 一级分类行 */}
                        <tr className="bg-blue-50 hover:bg-blue-100">
                          <td className="px-4 py-3">
                            <div className="flex items-center space-x-2">
                              <button onClick={() => toggleExpand(parent.id)} className="text-gray-500">
                                {isExpanded
                                  ? <ChevronDown className="w-4 h-4" />
                                  : <ChevronRight className="w-4 h-4" />}
                              </button>
                              <span className="font-semibold text-blue-700">{getCategoryName(parent)}</span>
                              <span className="text-xs text-blue-400">({children.length} 个子分类)</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {parentStat.product_count || 0}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">{parentStat.total_stock || 0}</td>
                          <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">
                            ${parseFloat(parentStat.total_value || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center space-x-1">
                              {/* 新增子分类 */}
                              <button onClick={() => handleAdd(parent.id)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                                title="新增子分类">
                                <Plus className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleEdit(parent)}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(parent.id, getCategoryName(parent), parentStat.product_count, children.length)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                disabled={children.length > 0 || parentStat.product_count > 0}>
                                <Trash2 className={`w-4 h-4 ${(children.length > 0 || parentStat.product_count > 0) ? 'opacity-30' : ''}`} />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* 二级分类行 */}
                        {isExpanded && children.map(child => {
                          const childStat = getStat(child.id);
                          return (
                            <tr key={child.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2 pl-8">
                                  <span className="text-gray-300 text-lg">└</span>
                                  <span className="text-sm text-gray-700">{getCategoryName(child)}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {childStat.product_count || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-600">{childStat.total_stock || 0}</td>
                              <td className="px-4 py-3 text-sm text-right text-blue-600">
                                ${parseFloat(childStat.total_value || 0).toFixed(2)}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center space-x-1">
                                  <button onClick={() => handleEdit(child)}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(child.id, getCategoryName(child), childStat.product_count)}
                                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    disabled={childStat.product_count > 0}>
                                    <Trash2 className={`w-4 h-4 ${childStat.product_count > 0 ? 'opacity-30' : ''}`} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDialog && (
        <CategoryDialog
          category={editingCategory}
          parentId={defaultParentId}
          parentCategories={treeData}
          onClose={() => { setShowDialog(false); setEditingCategory(null); }}
          onSubmit={handleSubmit}
        />
      )}

      {showImportDialog && (
        <CategoryImportDialog
          onClose={() => setShowImportDialog(false)}
          onImported={loadCategories}
        />
      )}
    </div>
  );
};

export default CategoriesPage;